import PropTypes from "prop-types";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";

// components
import RadioInput from "@/components/RadioInput/RadioInput";
import Checkbox from "@/components/Checkbox/Checkbox";
import Stepper from "@/components/Stepper/Stepper";
import BrainCoverageViz from "@/components/BrainCoverageViz/BrainCoverageViz";
import StepperPresets from "@/components/Stepper/StepperPresets";

// -----------------------------------------------------------------------------

export default function Neurodynamics({ isLinkMode, expId }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;

	// Select the experiment data from the store
	const experiment = useStore((state) =>
		state.neurodynamicsExperimentsArr.find((exp) => exp.id === expId),
	);

	// Select each action individually
	const setNeurodynamicsValues = useStore(
		(state) => state.setNeurodynamicsValues,
	);

	// -----------------------------------------------------------------------------

	return (
		<div className="experiment-container">
			{/* Brain coverage stepper */}
			<div className="stepper-w-viz">
				<Stepper
					label="Brain Coverage [%]"
					id={`neurodynamics-coverage-${expId}`}
					value={experiment.neurodynamicsCoverage}
					min={0}
					max={100}
					step={5}
					onChange={(value) =>
						setNeurodynamicsValues(expId, "neurodynamicsCoverage", value)
					}
				/>
				{/* Icon */}
				<BrainCoverageViz coverage={experiment.neurodynamicsCoverage} />
				{/* Presets */}
				<StepperPresets
					label="Brain Coverage [%]"
					presets={[25, 75, 100]}
					onChange={(value) =>
						setNeurodynamicsValues(expId, "neurodynamicsCoverage", value)
					}
				/>
			</div>

			{/* Recording Resolution options */}
			<fieldset className="radio-group">
				<legend>Recording Resolution</legend>
				<RadioInput
					label="Brain area"
					value="brain-area"
					name={`recording-resolution-${expId}`}
					checked={experiment.recordingResolution === "brain-area"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "recordingResolution", e.target.value)
					}
				/>
				<RadioInput
					label="1000s of neurons"
					value="thousands-neurons"
					name={`recording-resolution-${expId}`}
					checked={experiment.recordingResolution === "thousands-neurons"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "recordingResolution", e.target.value)
					}
				/>
				<RadioInput
					label="Single neurons"
					value="single-neurons"
					name={`recording-resolution-${expId}`}
					checked={experiment.recordingResolution === "single-neurons"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "recordingResolution", e.target.value)
					}
				/>
			</fieldset>

			{/* Recording speed stepper */}
			<Stepper
				label="Recording Speed [Hz]"
				id={`recording-speed-${expId}`}
				value={experiment.recordingSpeed}
				min={0}
				max={10000}
				step={100}
				onChange={(value) =>
					setNeurodynamicsValues(expId, "recordingSpeed", value)
				}
			/>

			{/* Behavior during recording options */}
			<fieldset className="radio-group">
				<legend>Behavior During Recording</legend>
				<RadioInput
					label="Fixated"
					value="fixated"
					name={`behavior-${expId}`}
					checked={experiment.behavior === "fixated"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "behavior", e.target.value)
					}
				/>
				<RadioInput
					label="Moving"
					value="moving"
					name={`behavior-${expId}`}
					checked={experiment.behavior === "moving"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "behavior", e.target.value)
					}
				/>
				<RadioInput
					label="Highly complex behavior"
					value="complex-behavior"
					name={`behavior-${expId}`}
					checked={experiment.behavior === "complex-behavior"}
					onChange={(e) =>
						setNeurodynamicsValues(expId, "behavior", e.target.value)
					}
				/>
			</fieldset>

			{/* Total Hours */}
			<Stepper
				label="Total Hours"
				id={`total-hours-${expId}`}
				value={experiment.totalHours}
				min={1}
				max={10000}
				step={1}
				onChange={(value) => setNeurodynamicsValues(expId, "totalHours", value)}
			/>

			{/* Optional options */}
			<fieldset>
				<legend>Optional</legend>
				<Checkbox
					label="Neuromodulatory effects"
					value="neuromodulatory-effects"
					// added id so that console doesn't complain
					id={`neuromodulatory-effects-${expId}`}
					checked={experiment.neuromodulatoryEffects}
					onChange={(e) =>
						setNeurodynamicsValues(
							expId,
							"neuromodulatoryEffects",
							e.target.checked,
						)
					}
				/>
			</fieldset>
		</div>
	);
}

// -----------------------------------------------------------------------------

Neurodynamics.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
	expId: PropTypes.string.isRequired,
};
